import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { RotaButton, RotaDivider, RotaSnackBar } from "@teamrota/rota-design";

import useAuth from "~/src/auth/hooks/use-auth";
import { APPROVAL_STATES } from "~/src/consts";

import { useCurrency } from "~/src/containers/profile-loader";
import { APPROVE_SHIFT } from "~/src/containers/timesheets/graphql/approve-shift.js";
import { validateApprovals } from "~/src/containers/timesheets/helpers.js";

import { errorModal } from "~/src/utils/errors";
import { getFormatterMoney } from "~/src/utils/formatting";
import { hoursAndMinutesDisplay } from "~/src/utils/formatting-hours-timesheet";

import TimesheetRow from "./components/timesheet-row";
import {
  EmptyBookings,
  StyledBoldText,
  StyledOrangeText,
  StyledShiftTotal,
  StyledStatus,
  StyledTotalHoursWrapper,
  StyledWrapInfo,
  StyledWrapShiftInfo,
  StyledWrapShiftResult,
  StyledWrapShiftStatus,
  StyledWrapShiftValue,
  StyledWrapTimesheetRow,
  Wrapper
} from "./timesheet.styles";

const Timesheet = ({ sourceAccountId, shift, shiftInfo, refetch }) => {
  if (!shift?.account?.shiftById) return null;

  const {
    bookings,
    chargeRate,
    totalVat,
    totalWithBonus,
    totalWithVat,
    extraHours,
    extraPaymentValue,
    extraPaymentRate,
    id,
    isUncalculatedRate,
    isDayRate
  } = shift?.account?.shiftById;

  const { currencyCode, taxRate } = useCurrency();

  const formatterMoney = getFormatterMoney(currencyCode);

  const auth = useAuth();
  const [bookingUpdates, setBookingUpdates] = useState([]);
  const [snackbar, setSnackBar] = useState({});
  const [isApproved, setIsApproved] = useState(
    shiftInfo?.approvalState === APPROVAL_STATES.APPROVED
  );
  const [isApproveEnabled, setIsApproveEnabled] = useState(0);
  const isUpcoming = shiftInfo?.approvalState === APPROVAL_STATES.UPCOMING;

  const {
    isCasualMemberTypeRatings,
    isPermanentMemberTypeRatings,
    isAgencyMemberTypeRatings,
    isContractorLimitedCompanyMemberTypeRatings,
    isContractorUmbrellaCompanyMemberTypeRatings,
    isContractorSelfEmployedMemberTypeRatings
  } = shift.account;

  const isRatingRequired =
    isCasualMemberTypeRatings ||
    isPermanentMemberTypeRatings ||
    isAgencyMemberTypeRatings ||
    isContractorLimitedCompanyMemberTypeRatings ||
    isContractorUmbrellaCompanyMemberTypeRatings ||
    isContractorSelfEmployedMemberTypeRatings;

  const [approveShift] = useMutation(APPROVE_SHIFT);

  const handleApprove = async ({ isApprovingShift, bookingForUpdate }) => {
    const success = isApprovingShift ? "Approved" : "Saved";
    // those without access to pay/charges can't see this field
    // so we have to do without this check in that circumstance

    if (!totalWithVat || totalWithVat >= 0) {
      const error = validateApprovals({
        bookingUpdates: isApprovingShift ? bookingUpdates : [bookingForUpdate],
        isApprovingShift,
        isRatingRequired
      });
      if (error) {
        errorModal(error);
        return true;
      } else {
        try {
          await approveShift({
            variables: auth.addVals(APPROVE_SHIFT, {
              shiftId: id,
              bookings: isApprovingShift ? bookingUpdates : [bookingForUpdate],
              sourceAccountId,
              isApproved: isApprovingShift
            })
          });
          refetch();
          setSnackBar({
            open: true,
            message: `${success} successfully.`,
            severity: "success"
          });
          isApprovingShift && setIsApproved(true);
        } catch (e) {
          setSnackBar({
            open: true,
            message: `Something went wrong. Please try again`,
            severity: "error"
          });
        }
        return false;
      }
    } else {
      errorModal(
        "There is a problem with this timesheet, please check that the hours are correct"
      );
    }
  };

  if (!bookings?.length) {
    return <EmptyBookings> No Bookings </EmptyBookings>;
  }

  const sortedBookings = [...bookings]?.sort((a, b) => {
    if (a.member.firstName > b.member.firstName) {
      return 1;
    }
    if (a.member.firstName < b.member.firstName) {
      return -1;
    }
    return 0;
  });

  return (
    <Wrapper>
      <StyledWrapTimesheetRow>
        {sortedBookings.map(booking => (
          <TimesheetRow
            bookingData={booking}
            key={booking?.member.id}
            setBookingUpdate={setBookingUpdates}
            handleApprove={handleApprove}
            isApproved={isApproved}
            isUncalculatedRate={isUncalculatedRate}
            setIsApproveEnabled={setIsApproveEnabled}
          />
        ))}
        <StyledWrapShiftInfo>
          <StyledWrapShiftStatus>
            Member Acknowledgement:{" "}
            {isApproved ? (
              <StyledStatus isApproved>&nbsp; Approved</StyledStatus>
            ) : (
              <StyledStatus>&nbsp; Pending</StyledStatus>
            )}
          </StyledWrapShiftStatus>
          <StyledWrapInfo>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ fontWeight: 400 }}>Note:&nbsp;</span>
              <p>Salaried workers payment is not included in the total</p>
            </div>
          </StyledWrapInfo>
          <StyledWrapShiftResult>
            {isUncalculatedRate && (
              <>
                <StyledTotalHoursWrapper>
                  <span>Total Worked Hours</span>
                  <StyledOrangeText>
                    {hoursAndMinutesDisplay(shift?.account?.shiftById)}
                  </StyledOrangeText>
                </StyledTotalHoursWrapper>
                <RotaDivider />
                <StyledBoldText>
                  Pay for this shift will be calculated in Payroll download.
                </StyledBoldText>
              </>
            )}
            {!isUncalculatedRate && (
              <>
                <StyledWrapShiftValue>
                  {hoursAndMinutesDisplay(shift?.account?.shiftById)}{" "}
                  {isDayRate ? "@" : "x"} &nbsp;
                  {formatterMoney.format(chargeRate / 100)} =&nbsp;
                  <span style={{ fontWeight: 700 }}>
                    {formatterMoney.format(totalWithBonus / 100)}
                  </span>
                </StyledWrapShiftValue>
                <StyledWrapShiftValue top>
                  {hoursAndMinutesDisplay(
                    shift?.account?.shiftById,
                    extraHours
                  )}{" "}
                  {isDayRate ? "@" : "x"} &nbsp;
                  {formatterMoney.format(extraPaymentRate / 100)}
                  &nbsp;=&nbsp;
                  <span style={{ fontWeight: 700 }}>
                    {formatterMoney.format(extraPaymentValue / 100)}
                  </span>
                </StyledWrapShiftValue>
                <StyledWrapShiftValue top>
                  VAT ({taxRate}%) =&nbsp;
                  <span style={{ fontWeight: 700 }}>
                    {formatterMoney.format(totalVat / 100)}
                  </span>
                </StyledWrapShiftValue>
                <StyledShiftTotal>
                  <StyledOrangeText>Total</StyledOrangeText>
                  {formatterMoney.format(totalWithVat / 100)}
                </StyledShiftTotal>
              </>
            )}
            {!isApproved && !isUpcoming && (
              <StyledWrapShiftValue style={{ marginTop: "12px" }}>
                <RotaButton
                  disabled={isApproveEnabled}
                  onClick={() => {
                    handleApprove({ isApprovingShift: true });
                  }}
                >
                  Approve
                </RotaButton>
              </StyledWrapShiftValue>
            )}
          </StyledWrapShiftResult>
        </StyledWrapShiftInfo>
      </StyledWrapTimesheetRow>
      <RotaSnackBar
        snackOpen={snackbar.open}
        onClose={() => setSnackBar({})}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Wrapper>
  );
};

export default Timesheet;
